/* eslint-disable react-hooks/exhaustive-deps */
import type { TDelinkAccountResponse } from "@apis/accounts/types"
import {
  useAccount,
  useDelinkAccountMutation,
} from "@apis/accounts/useAccounts"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { Button } from "@components/ui/button"
import { useDrawer } from "@contexts/DrawerContext"
import { Loader2, MoveRight } from "lucide-react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

export const DelinkAccount = ({ account_id }: { account_id: string }) => {
  const { data: userInfo } = useAuthUserDetails()
  const delinkAccountMutation = useDelinkAccountMutation()
  const getAccountQuery = useAccount(account_id)
  const [profileInfo, setProfileInfo] = useState<TDelinkAccountResponse | null>(
    null,
  )
  const [newMembershipProfileId, setNewMembershipProfileId] = useState<
    string | null
  >(null)
  const { closeDrawer } = useDrawer()

  // this useEffect was implemented because the backend returns data to be displayed
  // before the user can confirm they want to delink the account
  useEffect(() => {
    delinkAccountMutation.mutate(
      {
        account_id: account_id,
        request: { confirm: false },
      },
      {
        onSuccess: (data) => {
          setProfileInfo(data)
        },
      },
    )
  }, [])

  if (
    getAccountQuery.isPending ||
    (!profileInfo && delinkAccountMutation.isPending)
  ) {
    return (
      <div className="tw:flex tw:justify-center tw:py-10">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )
  }

  if (delinkAccountMutation.isError || getAccountQuery.isError) {
    return (
      <div className="tw:container tw:mt-6 tw:text-red-500">
        There was an error loading account or membership details. Error:{" "}
        {delinkAccountMutation.error?.message}
        {getAccountQuery.error?.message}
      </div>
    )
  }

  const confirmAccountDelink = () => {
    delinkAccountMutation.mutate(
      {
        account_id: account_id,
        request: { confirm: true, user_id: userInfo?.user_id },
      },
      {
        onSuccess: (data) => {
          toast("Successfully delinked account")
          setNewMembershipProfileId(data.membership_profile_id)
        },
      },
    )
  }

  return (
    <div>
      {!newMembershipProfileId ? (
        <div className="tw:flex tw:flex-col tw:gap-4">
          <div>
            <h4>
              This will create a new profile for{" "}
              <Link
                to={`/profiles/${getAccountQuery.data?.membership_profile?.membership_profile_id}/accounts/${account_id}`}
                target="_blank"
                rel="noreferrer"
                className="tw:text-blue-500"
              >
                Account {getAccountQuery.data?.account_id}.
              </Link>
            </h4>
            <p>
              The following information is based on what was provided at the
              point of sale. We will use this information to create a new
              profile for this account.
            </p>
          </div>

          <div>
            <p className="tw:m-0">
              <strong>First name:</strong> {profileInfo?.first_name}
            </p>
            <p className="tw:m-0">
              <strong>Last name:</strong> {profileInfo?.last_name}
            </p>
            <p className="tw:m-0">
              <strong>Email address:</strong> {profileInfo?.email_address}
            </p>
            <p className="tw:m-0">
              <strong>Phone number:</strong> {profileInfo?.primary_phone}
            </p>
          </div>
          <div className="tw:flex tw:flex-col tw:items-start tw:gap-2">
            <Button
              variant="destructive"
              onClick={confirmAccountDelink}
              disabled={delinkAccountMutation.isPending}
            >
              {delinkAccountMutation.isPending ? (
                <>
                  <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />{" "}
                  Delinking Account
                </>
              ) : (
                "Confirm"
              )}
            </Button>
            <p className="tw:text-red-600">
              This is irreversible, so be sure this is not the main account for
              the profile.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <h4>Successfully created new profile!</h4>
          <Button variant="link" className="tw:has-[>svg]:p-0" asChild>
            <Link
              to={`/profiles/${newMembershipProfileId}`}
              onClick={closeDrawer}
            >
              Go to new profile page <MoveRight />
            </Link>
          </Button>
        </div>
      )}
    </div>
  )
}
